import { useEffect, useState } from "react"

import { InteractionStatus } from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"
import { useNavigate } from "react-router-dom"

import {
  AuthMode,
  getAuthMode,
  initializeAuth,
  registerAuthSessionForCurrentMode,
} from "@/api/auth-api"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"

import { getAuthAdapter } from "../adapters/auth-adapter"

export function useAuth() {
  // useMsal() must always be called (React rules of hooks), but it returns a stub
  // when not inside MsalProvider. We guard against using the stub in the auth flow.
  const { instance, inProgress } = useMsal()

  const navigate = useNavigate()
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false)
  const [isRedirectingToLogin, setIsRedirectingToLogin] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function setupAuth() {
      logger.debug("Setting up authentication", { component: "useAuth" })

      try {
        const authMode = await initializeAuth()
        const adapter = getAuthAdapter(authMode)

        // For EasyAuth, we don't need MSAL instance at all - pass a dummy context
        // that the easyAuthAdapter will ignore anyway.
        const authContext =
          authMode === AuthMode.EASYAUTH
            ? { msalInstance: null as never, inProgress: InteractionStatus.None }
            : { msalInstance: instance, inProgress }

        const outcome = await adapter.ensureAuthenticated(authContext)

        if (cancelled) return

        if (outcome.status === "authenticated") {
          // Hand the refresh coordinator the identity this session actually
          // authenticated as, so a 401 can be recovered for that exact account
          // (#2904). Idempotent across effect reruns; a genuinely different
          // account resets the coordinator's state.
          const isEasyAuth = authMode === AuthMode.EASYAUTH
          SettingsService.createInstance(outcome.token, outcome.usingMsalBackup, () => {
            registerAuthSessionForCurrentMode({
              msalInstance: isEasyAuth ? null : instance,
              account: isEasyAuth ? null : instance.getActiveAccount(),
              publishCredential: (token) => SettingsService.publishToken(token),
              readCredential: () => SettingsService.peekToken(),
            })
          })

          setIsUserAuthenticated(true)
          setIsRedirectingToLogin(false)
          return
        }

        if (outcome.status === "redirecting") {
          setIsUserAuthenticated(false)
          setIsRedirectingToLogin(true)
          return
        }

        logger.error("Authentication failed", outcome)
        setIsUserAuthenticated(false)
        setIsRedirectingToLogin(false)
        void navigate("/auth/error", { replace: true })
      } catch (error) {
        logger.error("Unexpected error during authentication setup", error as Error)
        if (!cancelled) {
          void navigate("/auth/error", { replace: true })
        }
      }
    }

    void setupAuth()

    return () => {
      cancelled = true
    }
  }, [inProgress, instance, navigate])

  return {
    isUserAuthenticated,
    isRedirectingToLogin,
    authMode: getAuthMode(),
  }
}
